/**
 * Auth Context for NLT Radio
 * Handles user authentication state
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const existingToken = localStorage.getItem('token');
      if (existingToken) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${existingToken}` },
            timeout: 20000,
          });
          setUser(response.data);
          setToken(existingToken);
          setIsAuthenticated(true);
        } catch (error) {
          const status = error?.response?.status;
          // Only log the user out on a genuine auth rejection. Transient issues
          // (network blip, timeout, 5xx behind Cloudflare) must NOT wipe the token,
          // otherwise users get "randomly logged out" and forced to sign in again.
          if (status === 401 || status === 403) {
            localStorage.removeItem('token');
            setUser(null);
            setToken(null);
            setIsAuthenticated(false);
          } else {
            console.warn('Auth check could not reach server; keeping session token for retry.', status);
            // Keep the token so the next load can re-verify.
            setToken(existingToken);
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        identifier: email,  // Backend expects 'identifier' field
        password
      });
      
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      // Extract error message from various formats
      let errorMessage = 'Login failed';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || String(e)).join(', ');
        } else if (detail.msg) {
          errorMessage = detail.msg;
        }
      }
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }, []);

  // Register
  const register = useCallback(async (name, email, password) => {
    try {
      // Generate username from email (before @) or use name
      const username = email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '') + Math.floor(Math.random() * 1000);
      
      const response = await axios.post(`${API_URL}/api/auth/signup`, {
        name,
        email,
        password,
        username
      });
      
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Registration failed:', error);
      // Extract error message from various formats
      let errorMessage = 'Registration failed';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || String(e)).join(', ');
        } else if (detail.msg) {
          errorMessage = detail.msg;
        }
      }
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }, []);

  // Establish a session from an existing token (used by the Google OAuth callback).
  // This updates the SHARED auth state so the app is authenticated immediately,
  // avoiding the race where the callback set localStorage but the provider's
  // one-time checkAuth had already run (causing a "sign in again" bounce).
  const loginWithToken = useCallback(async (newToken) => {
    if (!newToken) return { success: false, error: 'Missing token' };
    try {
      localStorage.setItem('token', newToken);
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setToken(newToken);
      setUser(response.data);
      setIsAuthenticated(true);
      return { success: true, user: response.data };
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      return { success: false, error: 'Failed to establish session' };
    }
  }, []);

  // Google OAuth
  const loginWithGoogle = useCallback(async (credential) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/google`, {
        credential
      });
      
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Google login failed:', error);
      // Extract error message from various formats
      let errorMessage = 'Google login failed';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || String(e)).join(', ');
        } else if (detail.msg) {
          errorMessage = detail.msg;
        }
      }
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Update user profile
  const updateProfile = useCallback(async (updates) => {
    const token = localStorage.getItem('token');
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const response = await axios.put(
        `${API_URL}/api/users/me`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Profile update failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Update failed' 
      };
    }
  }, []);

  const patchUser = useCallback((fields) => {
    setUser((prev) => (prev ? { ...prev, ...fields } : prev));
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    loginWithGoogle,
    loginWithToken,
    logout,
    updateProfile,
    patchUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
